// =============================================================================
// Milestone Repository - Data Layer
// Handles all Supabase operations for project milestones
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import { fetchTerminalStatusCodes } from './statusRepository';

export type MilestoneStatus = 'open' | 'in_progress' | 'done';

// =============================================================================
// Search Result Type for Command Palette
// =============================================================================
export interface MilestoneSearchResult {
  id: string;
  title: string;
  project_id: string;
  project_name: string;
  status: MilestoneStatus;
}

export interface Milestone {
  id: string;
  org_id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: MilestoneStatus;
  start_date: string | null;
  due_date: string | null;
  color: string | null;
  position: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MilestoneCreateInput {
  org_id: string;
  project_id: string;
  title: string;
  description?: string | null;
  status?: MilestoneStatus;
  start_date?: string | null;
  due_date?: string | null;
  color?: string | null;
  created_by?: string | null;
}

export interface MilestoneUpdateInput {
  title?: string;
  description?: string | null;
  status?: MilestoneStatus;
  start_date?: string | null;
  due_date?: string | null;
  color?: string | null;
}

export interface MilestoneProgress {
  totalTasks: number;
  doneTasks: number;
  percent: number;
}

export interface ProjectProgress {
  mode: 'milestones' | 'tasks';
  done: number;
  total: number;
  percent: number;
}

function toPercent(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((done / total) * 100);
}

function buildMilestoneProgress(taskStatusCodes: string[], terminalStatusCodes: string[]): MilestoneProgress {
  const terminalCodes = new Set(terminalStatusCodes);
  const totalTasks = taskStatusCodes.length;
  const doneTasks = taskStatusCodes.filter((code) => terminalCodes.has(code)).length;

  return {
    totalTasks,
    doneTasks,
    percent: toPercent(doneTasks, totalTasks),
  };
}

function buildProjectProgressFromMilestones(
  milestones: Array<{ status: MilestoneStatus | string | null }>
): ProjectProgress {
  const total = milestones.length;
  const done = milestones.filter((milestone) => milestone.status === 'done').length;

  return {
    mode: 'milestones',
    done,
    total,
    percent: toPercent(done, total),
  };
}

function buildProjectProgressFromTasks(taskStatusCodes: string[], terminalStatusCodes: string[]): ProjectProgress {
  const terminalCodes = new Set(terminalStatusCodes);
  const total = taskStatusCodes.length;
  const done = taskStatusCodes.filter((code) => terminalCodes.has(code)).length;

  return {
    mode: 'tasks',
    done,
    total,
    percent: toPercent(done, total),
  };
}

// =============================================================================
// Fetch all milestones for a project
// =============================================================================
export async function fetchProjectMilestones(projectId: string): Promise<Milestone[]> {
  const { data, error } = await supabase
    .from('project_milestones')
    .select('*')
    .eq('project_id', projectId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[milestoneRepository] fetchProjectMilestones error:', error);
    throw error;
  }

  return (data || []).map(m => ({
    ...m,
    status: m.status as MilestoneStatus,
    position: m.position ?? 0,
  }));
}

// =============================================================================
// Create a milestone
// =============================================================================
export async function createMilestone(input: MilestoneCreateInput): Promise<Milestone> {
  // Get max position for this project
  const { data: existingMilestones } = await supabase
    .from('project_milestones')
    .select('position')
    .eq('project_id', input.project_id)
    .order('position', { ascending: false })
    .limit(1);
  
  const maxPosition = existingMilestones?.[0]?.position ?? -1;
  
  const { data, error } = await supabase
    .from('project_milestones')
    .insert({
      org_id: input.org_id,
      project_id: input.project_id,
      title: input.title,
      description: input.description ?? null,
      status: input.status ?? 'open',
      start_date: input.start_date ?? null,
      due_date: input.due_date ?? null,
      color: input.color ?? null,
      position: maxPosition + 1,
      created_by: input.created_by ?? null,
    })
    .select()
    .single();

  if (error) {
    console.error('[milestoneRepository] createMilestone error:', error);
    throw error;
  }

  return {
    ...data,
    status: data.status as MilestoneStatus,
    position: data.position ?? 0,
  };
}

// =============================================================================
// Update a milestone
// =============================================================================
export async function updateMilestone(id: string, patch: MilestoneUpdateInput): Promise<Milestone> {
  const { data, error } = await supabase
    .from('project_milestones')
    .update(patch)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[milestoneRepository] updateMilestone error:', error);
    throw error;
  }

  return {
    ...data,
    status: data.status as MilestoneStatus,
    position: data.position ?? 0,
  };
}

// =============================================================================
// Update milestone positions (for reordering)
// =============================================================================
export async function updateMilestonePositions(milestones: { id: string; position: number }[]): Promise<void> {
  // Update positions one by one (could be optimized with a batch update if needed)
  for (const m of milestones) {
    const { error } = await supabase
      .from('project_milestones')
      .update({ position: m.position })
      .eq('id', m.id);
    
    if (error) {
      console.error('[milestoneRepository] updateMilestonePositions error:', error);
      throw error;
    }
  }
}

// =============================================================================
// Delete a milestone
// =============================================================================
export async function deleteMilestone(id: string): Promise<void> {
  const { error } = await supabase
    .from('project_milestones')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[milestoneRepository] deleteMilestone error:', error);
    throw error;
  }
}

// =============================================================================
// Assign a milestone to a task (or unassign with null)
// =============================================================================
export async function assignMilestoneToTask(taskId: string, milestoneId: string | null): Promise<void> {
  const { error } = await supabase
    .from('project_tasks')
    .update({ milestone_id: milestoneId })
    .eq('id', taskId);

  if (error) {
    console.error('[milestoneRepository] assignMilestoneToTask error:', error);
    throw error;
  }
}

// =============================================================================
// Fetch tasks by milestone
// =============================================================================
export async function fetchTasksByMilestone(projectId: string, milestoneId: string) {
  const { data, error } = await supabase
    .from('project_tasks')
    .select('*')
    .eq('project_id', projectId)
    .eq('milestone_id', milestoneId)
    .is('parent_task_id', null) // Only parent tasks
    .order('kanban_position', { ascending: true });

  if (error) {
    console.error('[milestoneRepository] fetchTasksByMilestone error:', error);
    throw error;
  }

  return data || [];
}

// =============================================================================
// Fetch milestone progress (direct query + backend status recompute)
// =============================================================================
export async function fetchMilestoneProgress(
  milestoneId: string,
  terminalStatusCodesOverride?: string[]
): Promise<MilestoneProgress> {
  const { data: milestone, error: milestoneError } = await supabase
    .from('project_milestones')
    .select('org_id, project_id')
    .eq('id', milestoneId)
    .maybeSingle();

  if (milestoneError) {
    console.error('[milestoneRepository] fetchMilestoneProgress milestone lookup error:', milestoneError);
    throw milestoneError;
  }

  if (!milestone) {
    return { totalTasks: 0, doneTasks: 0, percent: 0 };
  }

  const tasksPromise = supabase
    .from('project_tasks')
    .select('status_code')
    .eq('milestone_id', milestoneId)
    .is('parent_task_id', null);

  const [terminalStatusCodes, tasksResult] = await Promise.all([
    terminalStatusCodesOverride && terminalStatusCodesOverride.length > 0
      ? Promise.resolve(terminalStatusCodesOverride)
      : fetchTerminalStatusCodes(milestone.org_id, milestone.project_id),
    tasksPromise,
  ]);

  if (tasksResult.error) {
    console.error('[milestoneRepository] fetchMilestoneProgress tasks lookup error:', tasksResult.error);
    throw tasksResult.error;
  }

  const taskStatusCodes = (tasksResult.data || []).map((task) => task.status_code);
  return buildMilestoneProgress(taskStatusCodes, terminalStatusCodes);
}

// =============================================================================
// Fetch project progress (direct query, no RPC dependency)
// =============================================================================
export async function fetchProjectProgress(
  projectId: string,
  terminalStatusCodesOverride?: string[]
): Promise<ProjectProgress> {
  const { data: milestones, error: milestonesError } = await supabase
    .from('project_milestones')
    .select('status')
    .eq('project_id', projectId);

  if (milestonesError) {
    console.error('[milestoneRepository] fetchProjectProgress milestones lookup error:', milestonesError);
    throw milestonesError;
  }

  if ((milestones || []).length > 0) {
    return buildProjectProgressFromMilestones(milestones || []);
  }

  const { data: project, error: projectError } = await supabase
    .from('project_projects')
    .select('org_id')
    .eq('id', projectId)
    .maybeSingle();

  if (projectError) {
    console.error('[milestoneRepository] fetchProjectProgress project lookup error:', projectError);
    throw projectError;
  }

  if (!project) {
    return { mode: 'tasks', done: 0, total: 0, percent: 0 };
  }

  const tasksPromise = supabase
    .from('project_tasks')
    .select('status_code')
    .eq('project_id', projectId)
    .is('parent_task_id', null);

  const [terminalStatusCodes, tasksResult] = await Promise.all([
    terminalStatusCodesOverride && terminalStatusCodesOverride.length > 0
      ? Promise.resolve(terminalStatusCodesOverride)
      : fetchTerminalStatusCodes(project.org_id, projectId),
    tasksPromise,
  ]);

  if (tasksResult.error) {
    console.error('[milestoneRepository] fetchProjectProgress tasks lookup error:', tasksResult.error);
    throw tasksResult.error;
  }

  const taskStatusCodes = (tasksResult.data || []).map((task) => task.status_code);
  return buildProjectProgressFromTasks(taskStatusCodes, terminalStatusCodes);
}

// =============================================================================
// Get a single milestone by ID
// =============================================================================
export async function fetchMilestoneById(milestoneId: string): Promise<Milestone | null> {
  const { data, error } = await supabase
    .from('project_milestones')
    .select('*')
    .eq('id', milestoneId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    console.error('[milestoneRepository] fetchMilestoneById error:', error);
    throw error;
  }

  return {
    ...data,
    status: data.status as MilestoneStatus,
  };
}

// =============================================================================
// Search milestones across all projects (for command palette)
// =============================================================================
export async function searchMilestones(
  orgId: string,
  query: string,
  limit: number = 5
): Promise<MilestoneSearchResult[]> {
  if (!query || query.trim().length === 0) return [];

  const { data, error } = await supabase
    .from('project_milestones')
    .select(`
      id,
      title,
      project_id,
      status,
      project_projects!inner (name)
    `)
    .eq('org_id', orgId)
    .ilike('title', `%${query}%`)
    .limit(limit);

  if (error) {
    console.error('[milestoneRepository] searchMilestones error:', error);
    throw error;
  }

  return (data || []).map((m: any) => ({
    id: m.id,
    title: m.title,
    project_id: m.project_id,
    project_name: m.project_projects?.name || '',
    status: m.status as MilestoneStatus,
  }));
}
